import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import './ProfileClub.css';

const API_ORIGIN = String(process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
const API_BASE = `${API_ORIGIN}/api`;

const LANGUAGE_LOCALES = {
  ru: 'ru-RU',
  en: 'en-US',
  ka: 'ka-GE',
  hy: 'hy-AM',
  az: 'az-AZ',
  it: 'it-IT',
  fr: 'fr-FR',
};

function getLanguageLocale(language) {
  const code = String(language || 'ru').toLowerCase().split('-')[0];
  return LANGUAGE_LOCALES[code] || LANGUAGE_LOCALES.ru;
}

export default function ProfileClub({ isPartner = false }) {
  const { t: translate, i18n } = useTranslation();
  const locale = getLanguageLocale(i18n.resolvedLanguage || i18n.language);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [clubsLoading, setClubsLoading] = useState(false);

  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const [clubInfo, setClubInfo] = useState({
    inClub: false,
    myRole: null,
    club: null,
  });

  const [clubBalance, setClubBalance] = useState(0);
  const [transfers, setTransfers] = useState([]);

  const [createName, setCreateName] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  const [createImage, setCreateImage] = useState(null);

  const [clubSearch, setClubSearch] = useState('');
  const [availableClubs, setAvailableClubs] = useState([]);
  const [joinCode, setJoinCode] = useState('');

  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editImage, setEditImage] = useState(null);

  const [transferAmounts, setTransferAmounts] = useState({});

  const [chatMode, setChatMode] =
    useState('club');

  const [clubChatMessages, setClubChatMessages] =
    useState([]);

  const [privateChatMessages, setPrivateChatMessages] =
    useState([]);

  const [chatText, setChatText] =
    useState('');

  const [selectedPrivateUserId, setSelectedPrivateUserId] =
    useState(null);

  const [chatLoading, setChatLoading] =
    useState(false);

  const [chatSending, setChatSending] =
    useState(false);

  const [permissionSavingId, setPermissionSavingId] =
    useState(null);

  const [tournamentPermissionConfirm, setTournamentPermissionConfirm] =
    useState(null);

  const token = localStorage.getItem('token');
  const authHeaders = token
    ? { Authorization: `Bearer ${token}` }
    : {};

  const clearMessages = () => {
    setError('');
    setNotice('');
  };

  const getErrorText = (err, fallback = translate('club.errors.generic')) => {
    return err?.response?.data?.message || err?.message || fallback;
  };

  const resolveImage = (raw) => {
    const value = String(raw || '').trim();

    if (!value) return null;
    if (/^https?:\/\//i.test(value)) return value;

    return `${API_ORIGIN}${value.startsWith('/') ? '' : '/'}${value}`;
  };

  const fetchTransfers = async () => {
    if (!token) return;

    try {
      const response = await axios.get(`${API_BASE}/clubs/transfers`, {
        headers: authHeaders,
      });

      setTransfers(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      if (err?.response?.status !== 403) {
        console.error('[ProfileClub] transfers error:', err);
      }

      setTransfers([]);
    }
  };

  const fetchAvailableClubs = async (search = '') => {
    if (!token) return;

    try {
      setClubsLoading(true);
      setError('');

      const response = await axios.get(`${API_BASE}/clubs/list`, {
        headers: authHeaders,
        params: {
          search: String(search || '').trim(),
        },
      });

      setAvailableClubs(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('[ProfileClub] clubs list error:', err);
      setAvailableClubs([]);
      setError(getErrorText(err, translate('club.errors.loadList')));
    } finally {
      setClubsLoading(false);
    }
  };

  const fetchClub = async () => {
    if (!token) {
      setLoading(false);
      setError(translate('club.errors.authRequired'));
      return;
    }

    try {
      setLoading(true);
      setError('');

      const response = await axios.get(`${API_BASE}/clubs/my`, {
        headers: authHeaders,
      });

      const data = response.data || {
        inClub: false,
        myRole: null,
        club: null,
        myBalance: 0,
      };

      setClubBalance(Number(data.myBalance || 0));
      setClubInfo(data);

      if (data.inClub && data.club) {
        setEditName(data.club.name || '');
        setEditDescription(data.club.description || '');

        if (data.myRole === 'captain') {
          await fetchTransfers();
        } else {
          setTransfers([]);
        }
      } else {
        setTransfers([]);
        await fetchAvailableClubs('');
      }
    } catch (err) {
      console.error('[ProfileClub] /clubs/my error:', err);
      setError(getErrorText(err, translate('club.errors.loadData')));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClub();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  useEffect(() => {
    if (
      !clubInfo.inClub ||
      !clubInfo.club
    ) {
      setSelectedPrivateUserId(
        null
      );

      setClubChatMessages([]);
      setPrivateChatMessages([]);

      return;
    }


    const ownerId =
      Number(
        clubInfo.club
          ?.owner?.id || 0
      );


    if (
      clubInfo.myRole !==
      'captain'
    ) {
      if (ownerId) {
        setSelectedPrivateUserId(
          ownerId
        );
      }

      return;
    }


    if (
      selectedPrivateUserId
    ) {
      return;
    }


    const firstMember =
      (clubInfo.club
        ?.members || [])
        .find(
          (item) =>
            Number(
              item?.user?.id
            ) !== ownerId
        );


    if (
      firstMember?.user?.id
    ) {
      setSelectedPrivateUserId(
        Number(
          firstMember.user.id
        )
      );
    }

  }, [
    clubInfo.inClub,
    clubInfo.club,
    clubInfo.myRole,
    selectedPrivateUserId,
  ]);


  useEffect(() => {
    if (
      !clubInfo.inClub ||
      !clubInfo.club
    ) {
      return undefined;
    }


    const loadChat = () => {
      if (
        chatMode ===
        'club'
      ) {
        fetchClubChat(true);
        return;
      }

      if (
        selectedPrivateUserId
      ) {
        fetchPrivateChat(
          selectedPrivateUserId,
          true
        );
      }
    };


    loadChat();


    const timer =
      window.setInterval(
        loadChat,
        2500
      );


    return () => {
      window.clearInterval(
        timer
      );
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    clubInfo.inClub,
    clubInfo.club?.id,
    chatMode,
    selectedPrivateUserId,
  ]);


  useEffect(() => {
    const oldBodyBackground = document.body.style.backgroundColor;
    const oldHtmlBackground = document.documentElement.style.backgroundColor;

    document.body.style.backgroundColor = '#020617';
    document.documentElement.style.backgroundColor = '#020617';

    return () => {
      document.body.style.backgroundColor = oldBodyBackground;
      document.documentElement.style.backgroundColor = oldHtmlBackground;
    };
  }, []);

  /*
    На всём сайте viewport отсутствует, потому что игровые доски рассчитаны
    на старую широкую вёрстку. Поэтому нормальный мобильный viewport включаем
    только пока открыта страница клуба, а при выходе возвращаем ширину 980.
  */
  useEffect(() => {
    let viewportMeta = document.querySelector('meta[name="viewport"]');
    const existedBefore = Boolean(viewportMeta);
    const previousContent = viewportMeta?.getAttribute('content') || null;

    if (!viewportMeta) {
      viewportMeta = document.createElement('meta');
      viewportMeta.name = 'viewport';
      document.head.appendChild(viewportMeta);
    }

    viewportMeta.setAttribute(
      'content',
      'width=device-width, initial-scale=1, viewport-fit=cover'
    );

    document.body.classList.add('club-mobile-page');

    const refreshLayout = () => {
      window.dispatchEvent(new Event('resize'));
    };

    refreshLayout();
    const timer = window.setTimeout(refreshLayout, 50);

    return () => {
      window.clearTimeout(timer);
      document.body.classList.remove('club-mobile-page');

      if (existedBefore && previousContent) {
        viewportMeta.setAttribute('content', previousContent);
      } else {
        viewportMeta.setAttribute('content', 'width=980');
      }

      refreshLayout();
    };
  }, []);

  const fetchClubChat = async (
    silent = false
  ) => {
    if (!token) return;

    try {
      if (!silent) {
        setChatLoading(true);
      }

      const response =
        await axios.get(
          `${API_BASE}/clubs/chat/club`,
          {
            headers:
              authHeaders,
          }
        );

      setClubChatMessages(
        Array.isArray(
          response.data?.messages
        )
          ? response.data.messages
          : []
      );


      if (
        response.data
          ?.myPermissions
      ) {
        setClubInfo(
          (previous) => ({
            ...previous,

            myPermissions:
              response.data
                .myPermissions,
          })
        );
      }

    } catch (err) {
      if (!silent) {
        console.error(
          '[ProfileClub] club chat error:',
          err
        );

        setError(
          getErrorText(
            err,
            translate('club.errors.loadClubChat')
          )
        );
      }
    } finally {
      if (!silent) {
        setChatLoading(false);
      }
    }
  };


  const fetchPrivateChat = async (
    targetUserId,
    silent = false
  ) => {
    const targetId =
      Number(targetUserId);

    if (
      !token ||
      !targetId
    ) {
      setPrivateChatMessages([]);
      return;
    }

    try {
      if (!silent) {
        setChatLoading(true);
      }

      const response =
        await axios.get(
          `${API_BASE}/clubs/chat/private/${targetId}`,
          {
            headers:
              authHeaders,
          }
        );

      setPrivateChatMessages(
        Array.isArray(
          response.data?.messages
        )
          ? response.data.messages
          : []
      );


      if (
        response.data
          ?.myPermissions
      ) {
        setClubInfo(
          (previous) => ({
            ...previous,

            myPermissions:
              response.data
                .myPermissions,
          })
        );
      }

    } catch (err) {
      if (!silent) {
        console.error(
          '[ProfileClub] private chat error:',
          err
        );

        setError(
          getErrorText(
            err,
            translate('club.errors.loadPrivateChat')
          )
        );
      }
    } finally {
      if (!silent) {
        setChatLoading(false);
      }
    }
  };


  const sendChatMessage = async () => {
    const text =
      chatText.trim();

    if (!text) {
      return;
    }

    try {
      setChatSending(true);
      setError('');

      let response;

      if (chatMode === 'club') {
        response =
          await axios.post(
            `${API_BASE}/clubs/chat/club`,
            {
              text,
            },
            {
              headers:
                authHeaders,
            }
          );

        const created =
          response.data?.message;

        if (created) {
          setClubChatMessages(
            (previous) => [
              ...previous,
              created,
            ]
          );
        }

      } else {
        const targetId =
          Number(
            selectedPrivateUserId
          );

        if (!targetId) {
          setError(
            translate('club.errors.selectMember')
          );

          return;
        }

        response =
          await axios.post(
            `${API_BASE}/clubs/chat/private/${targetId}`,
            {
              text,
            },
            {
              headers:
                authHeaders,
            }
          );

        const created =
          response.data?.message;

        if (created) {
          setPrivateChatMessages(
            (previous) => [
              ...previous,
              created,
            ]
          );
        }
      }

      setChatText('');

    } catch (err) {
      console.error(
        '[ProfileClub] send chat error:',
        err
      );

      setError(
        getErrorText(
          err,
          translate('club.errors.sendMessage')
        )
      );

    } finally {
      setChatSending(false);
    }
  };


  const handleMemberPermissions = async (
    member,
    patch
  ) => {
    const membershipId =
      Number(member?.id);

    if (!membershipId) {
      return;
    }

    try {
      setPermissionSavingId(
        membershipId
      );

      setError('');


      const response =
        await axios.patch(
          `${API_BASE}/clubs/members/${membershipId}/permissions`,
          patch,
          {
            headers:
              authHeaders,
          }
        );


      const permissions =
        response.data
          ?.permissions;


      if (permissions) {
        setClubInfo(
          (previous) => ({
            ...previous,

            club: {
              ...previous.club,

              members:
                (previous.club
                  ?.members || [])
                  .map(
                    (item) =>
                      Number(item.id) ===
                      membershipId
                        ? {
                            ...item,
                            permissions,
                          }
                        : item
                  ),
            },
          })
        );
      }

      return true;

    } catch (err) {
      console.error(
        '[ProfileClub] permissions error:',
        err
      );

      setError(
        getErrorText(
          err,
          translate('club.errors.changePermissions')
        )
      );

      return false;

    } finally {
      setPermissionSavingId(
        null
      );
    }
  };


  const handleClubTournamentPermissionClick = async (
    member
  ) => {
    const enabled =
      Boolean(
        member
          ?.permissions
          ?.canCreateClubTournaments
      );

    // Выключение безопасно и выполняется сразу.
    if (enabled) {
      await handleMemberPermissions(
        member,
        {
          canCreateClubTournaments:
            false,
        }
      );

      return;
    }

    // Включение сначала требует явного подтверждения капитана.
    setTournamentPermissionConfirm(
      member
    );
  };


  const confirmClubTournamentPermission = async () => {
    const member =
      tournamentPermissionConfirm;

    if (!member) {
      return;
    }

    const saved =
      await handleMemberPermissions(
        member,
        {
          canCreateClubTournaments:
            true,

          // Сервер тоже требует этот явный флаг.
          confirmCaptainFundsRisk:
            true,
        }
      );

    if (saved) {
      setTournamentPermissionConfirm(
        null
      );
    }
  };


  const openPrivateChat = (
    member
  ) => {
    const targetId =
      Number(
        member?.user?.id
      );

    if (!targetId) {
      return;
    }

    setSelectedPrivateUserId(
      targetId
    );

    setChatMode(
      'private'
    );

    setChatText('');

    fetchPrivateChat(
      targetId
    );


    window.setTimeout(
      () => {
        document
          .querySelector(
            '.club-chat-card'
          )
          ?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
      },
      50
    );
  };


  const uploadClubImage = async (file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);

    await axios.post(`${API_BASE}/clubs/image`, formData, {
      headers: authHeaders,
    });
  };

  const handleCreateClub = async () => {
    clearMessages();

    const name = createName.trim();

    if (name.length < 3 || name.length > 40) {
      setError(translate('club.errors.invalidName'));
      return;
    }

    try {
      setActionLoading(true);

      await axios.post(
        `${API_BASE}/clubs/create`,
        {
          name,
          description: createDescription.trim(),
        },
        { headers: authHeaders }
      );

      await uploadClubImage(createImage);

      setCreateName('');
      setCreateDescription('');
      setCreateImage(null);
      setNotice(translate('club.notices.created'));

      await fetchClub();
    } catch (err) {
      console.error('[ProfileClub] create error:', err);
      setError(getErrorText(err, translate('club.errors.createFailed')));
    } finally {
      setActionLoading(false);
    }
  };

  const handleJoinClub = async (clubId = null) => {
    clearMessages();

    let payload;

    if (clubId !== null && Number.isInteger(Number(clubId)) && Number(clubId) > 0) {
      payload = { clubId: Number(clubId) };
    } else {
      const code = joinCode.trim().toUpperCase();

      if (!code) {
        setError(translate('club.errors.enterCode'));
        return;
      }

      payload = { code };
    }

    try {
      setActionLoading(true);

      await axios.post(`${API_BASE}/clubs/join`, payload, {
        headers: authHeaders,
      });

      setJoinCode('');
      setNotice(translate('club.notices.joined'));

      await fetchClub();
    } catch (err) {
      console.error('[ProfileClub] join error:', err);
      setError(getErrorText(err, translate('club.errors.joinFailed')));
    } finally {
      setActionLoading(false);
    }
  };

  const handleLeaveClub = async () => {
    clearMessages();

    const confirmed = window.confirm(translate('club.confirm.leave'));
    if (!confirmed) return;

    try {
      setActionLoading(true);

      await axios.post(
        `${API_BASE}/clubs/leave`,
        {},
        { headers: authHeaders }
      );

      setNotice(translate('club.notices.left'));
      await fetchClub();
    } catch (err) {
      console.error('[ProfileClub] leave error:', err);
      setError(getErrorText(err, translate('club.errors.leaveFailed')));
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    clearMessages();

    const name = editName.trim();

    if (name.length < 3 || name.length > 40) {
      setError(translate('club.errors.invalidName'));
      return;
    }

    try {
      setActionLoading(true);

      await axios.patch(
        `${API_BASE}/clubs/settings`,
        {
          name,
          description: editDescription.trim(),
        },
        { headers: authHeaders }
      );

      await uploadClubImage(editImage);
      setEditImage(null);
      setNotice(translate('club.notices.saved'));

      await fetchClub();
    } catch (err) {
      console.error('[ProfileClub] settings error:', err);
      setError(getErrorText(err, translate('club.errors.saveFailed')));
    } finally {
      setActionLoading(false);
    }
  };

  const handleTransfer = async (member) => {
    clearMessages();

    const userId = Number(member?.user?.id);
    const receiverName = member?.user?.username || translate('club.roles.player');
    const amount = Number(transferAmounts[userId]);

    if (!userId) {
      setError(translate('club.errors.playerUnknown'));
      return;
    }

    if (!Number.isFinite(amount) || amount <= 0) {
      setError(translate('club.errors.invalidAmount'));
      return;
    }

    if (amount > Number(clubBalance || 0)) {
      setError(
        translate('club.errors.insufficientFunds', {
          balance: Number(clubBalance || 0).toFixed(2),
        })
      );
      return;
    }

    const confirmed = window.confirm(
      translate('club.confirm.transfer', {
        amount: amount.toFixed(2),
        name: receiverName,
      })
    );

    if (!confirmed) return;

    try {
      setActionLoading(true);

      const response = await axios.post(
        `${API_BASE}/clubs/transfer`,
        { userId, amount },
        { headers: authHeaders }
      );

      const data = response.data || {};

      setTransferAmounts((previous) => ({
        ...previous,
        [userId]: '',
      }));

      if (data.captainBalance !== undefined && data.captainBalance !== null) {
        const nextBalance = Number(data.captainBalance);
        setClubBalance(nextBalance);

        window.dispatchEvent(
          new CustomEvent('balance-updated', {
            detail: {
              balance: nextBalance,
              source: 'club-transfer',
              allowDecrease: true,
            },
          })
        );
      }

      setNotice(
        translate('club.notices.transferred', {
          name: data.receiver || receiverName,
          amount: Number(data.amount || amount).toFixed(2),
        })
      );

      await fetchTransfers();
    } catch (err) {
      console.error('[ProfileClub] transfer error:', err);
      setError(getErrorText(err, translate('club.errors.transferFailed')));
    } finally {
      setActionLoading(false);
    }
  };

  const club = clubInfo.club;
  const isCaptain = clubInfo.myRole === 'captain';
  const ownerId = Number(club?.owner?.id || 0);
  const clubImageUrl = resolveImage(club?.image_url);

  const myUserId =
    Number(
      clubInfo.myUserId || 0
    );

  const myPermissions =
    clubInfo.myPermissions || {
      canCreateClubTournaments:
        isCaptain,

      canWriteClubChat:
        true,

      canWritePrivateChat:
        true,

      chatBanned:
        false,
    };

  const canWriteCurrentChat =
    isCaptain
      ? true
      : (
          !myPermissions
            .chatBanned &&
          (
            chatMode === 'club'
              ? myPermissions
                  .canWriteClubChat
              : myPermissions
                  .canWritePrivateChat
          )
        );

  const currentChatMessages =
    chatMode === 'club'
      ? clubChatMessages
      : privateChatMessages;

  const selectedPrivateMember =
    (club?.members || [])
      .find(
        (item) =>
          Number(
            item?.user?.id
          ) ===
          Number(
            selectedPrivateUserId
          )
      );

  const privateChatTitle =
    isCaptain
      ? (
          selectedPrivateMember
            ?.user?.username ||
          translate('club.chat.selectMemberShort')
        )
      : (
          club?.owner
            ?.username ||
          translate('club.roles.captain')
        );

  const outer = {
    width: '100%',
    minWidth: 0,
    height: '100%',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    background: '#020617',
  };

  const scroll = {
    width: '100%',
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: '24px 24px 80px',
  };

  const content = {
    width: '100%',
    maxWidth: 1200,
    margin: '0 auto',
  };

  const card = {
    width: '100%',
    marginBottom: 24,
    padding: 20,
    borderRadius: 12,
    background: 'linear-gradient(135deg, #020617 0%, #020617 40%, #0b1220 100%)',
    border: '1px solid #1f2937',
    boxShadow: '0 18px 45px rgba(0,0,0,0.45)',
  };

  const input = {
    width: '100%',
    boxSizing: 'border-box',
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #374151',
    background: '#020617',
    color: '#e5e7eb',
    outline: 'none',
    fontSize: 14,
  };

  const blueButton = {
    padding: '9px 16px',
    borderRadius: 8,
    border: 'none',
    background: '#2563eb',
    color: '#fff',
    cursor: actionLoading ? 'not-allowed' : 'pointer',
    fontWeight: 600,
    opacity: actionLoading ? 0.6 : 1,
    whiteSpace: 'nowrap',
  };

  const secondaryButton = {
    ...blueButton,
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid #374151',
  };

  const redButton = {
    ...blueButton,
    background: 'rgba(239,68,68,0.14)',
    border: '1px solid rgba(239,68,68,0.45)',
    color: '#fecaca',
  };

  return (
    <div className="profile-club" style={outer}>
      <div className="profile-club-scroll" style={scroll}>
        <div className="profile-club-content" style={content}>
          <div style={{ marginBottom: 20 }}>
            <h2 style={{ margin: 0, fontSize: 24, color: '#e5e7eb' }}>
              {translate('club.page.title')}
            </h2>

            <div style={{ marginTop: 4, fontSize: 13, color: '#9ca3af' }}>
              {translate('club.page.subtitle')}
            </div>
          </div>

          {loading && (
            <div style={{ marginBottom: 16, color: '#9ca3af' }}>
              {translate('club.page.loading')}
            </div>
          )}

          {error && (
            <div
              style={{
                marginBottom: 16,
                padding: 12,
                borderRadius: 8,
                background: 'rgba(239,68,68,0.08)',
                border: '1px solid rgba(239,68,68,0.4)',
                color: '#fecaca',
              }}
            >
              {error}
            </div>
          )}

          {notice && (
            <div
              style={{
                marginBottom: 16,
                padding: 12,
                borderRadius: 8,
                background: 'rgba(34,197,94,0.08)',
                border: '1px solid rgba(34,197,94,0.4)',
                color: '#bbf7d0',
              }}
            >
              {notice}
            </div>
          )}

          {!loading && !clubInfo.inClub && (
            <>
              {isPartner && (
                <div className="profile-club-card" style={card}>
                  <h3 style={{ marginTop: 0, color: '#e5e7eb' }}>
                    {translate('club.create.title')}
                  </h3>

                  <p style={{ color: '#9ca3af', fontSize: 13 }}>
                    {translate('club.create.subtitle')}
                  </p>

                  <div style={{ display: 'grid', gap: 14, maxWidth: 760 }}>
                    <label>
                      <div style={labelStyle}>{translate('club.create.name')}</div>
                      <input
                        value={createName}
                        maxLength={40}
                        onChange={(event) => setCreateName(event.target.value)}
                        placeholder={translate('club.create.namePlaceholder')}
                        style={input}
                      />
                    </label>

                    <label>
                      <div style={labelStyle}>{translate('club.common.description')}</div>
                      <textarea
                        value={createDescription}
                        maxLength={500}
                        rows={4}
                        onChange={(event) => setCreateDescription(event.target.value)}
                        placeholder={translate('club.create.descriptionPlaceholder')}
                        style={{ ...input, resize: 'vertical' }}
                      />

                      <div style={counterStyle}>{createDescription.length}/500</div>
                    </label>

                    <label>
                      <div style={labelStyle}>{translate('club.create.emblem')}</div>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={(event) =>
                          setCreateImage(event.target.files?.[0] || null)
                        }
                        style={{ color: '#cbd5e1' }}
                      />
                    </label>

                    <div>
                      <button
                        type="button"
                        disabled={actionLoading}
                        onClick={handleCreateClub}
                        style={blueButton}
                      >
                        {actionLoading ? translate('club.create.creating') : translate('club.create.button')}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="profile-club-card" style={card}>
                <h3 style={{ marginTop: 0, marginBottom: 6, color: '#e5e7eb' }}>
                  {translate('club.find.title')}
                </h3>

                <div style={{ marginBottom: 16, color: '#9ca3af', fontSize: 13 }}>
                  {translate('club.find.subtitle')}
                </div>

                <div
                  className="club-mobile-stack"
                  style={{
                    display: 'flex',
                    gap: 10,
                    maxWidth: 650,
                    marginBottom: 20,
                  }}
                >
                  <input
                    value={clubSearch}
                    onChange={(event) => setClubSearch(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        fetchAvailableClubs(clubSearch);
                      }
                    }}
                    placeholder={translate('club.find.searchPlaceholder')}
                    style={input}
                  />

                  <button
                    type="button"
                    disabled={clubsLoading}
                    onClick={() => fetchAvailableClubs(clubSearch)}
                    style={blueButton}
                  >
                    {clubsLoading ? translate('club.find.searching') : translate('club.find.search')}
                  </button>

                  {clubSearch && (
                    <button
                      type="button"
                      onClick={() => {
                        setClubSearch('');
                        fetchAvailableClubs('');
                      }}
                      style={secondaryButton}
                    >
                      {translate('club.find.all')}
                    </button>
                  )}
                </div>

                {clubsLoading && (
                  <div style={{ padding: '10px 0', color: '#94a3b8', fontSize: 13 }}>
                    {translate('club.find.loading')}
                  </div>
                )}

                {!clubsLoading && availableClubs.length === 0 && (
                  <div style={{ padding: '12px 0', color: '#64748b', fontSize: 13 }}>
                    {translate('club.find.none')}
                  </div>
                )}

                {!clubsLoading && availableClubs.length > 0 && (
                  <div style={{ display: 'grid', gap: 10 }}>
                    {availableClubs.map((item) => {
                      const image = resolveImage(item.image_url);

                      return (
                        <div
                          key={item.id}
                          className="club-list-item"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 14,
                            padding: 14,
                            borderRadius: 12,
                            border: '1px solid #1f2937',
                            background: '#0b1220',
                          }}
                        >
                          <ClubImage image={image} name={item.name} size={64} />

                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              style={{
                                marginBottom: 4,
                                color: '#f1f5f9',
                                fontSize: 16,
                                fontWeight: 700,
                              }}
                            >
                              {item.name}
                            </div>

                            <div style={smallMutedStyle}>
                              {translate('club.common.captain')}:{' '}
                              <strong style={{ color: '#cbd5e1' }}>
                                {item.owner?.username || '—'}
                              </strong>
                            </div>

                            <div style={smallMutedStyle}>
                              {translate('club.common.members')}: {item.membersCount ?? 0}
                            </div>

                            {item.description && (
                              <div
                                style={{
                                  maxWidth: 600,
                                  marginTop: 6,
                                  overflow: 'hidden',
                                  color: '#64748b',
                                  fontSize: 12,
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {item.description}
                              </div>
                            )}
                          </div>

                          <button
                            type="button"
                            disabled={actionLoading}
                            onClick={() => handleJoinClub(item.id)}
                            style={blueButton}
                          >
                            {translate('club.find.join')}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div
                  style={{
                    marginTop: 22,
                    paddingTop: 18,
                    borderTop: '1px solid #1e293b',
                  }}
                >
                  <div style={{ marginBottom: 8, color: '#94a3b8', fontSize: 12 }}>
                    {translate('club.find.inviteQuestion')}
                  </div>

                  <div
                    className="club-mobile-stack"
                    style={{ display: 'flex', gap: 10, maxWidth: 550 }}
                  >
                    <input
                      value={joinCode}
                      onChange={(event) => setJoinCode(event.target.value.toUpperCase())}
                      placeholder={translate('club.find.codePlaceholder')}
                      style={input}
                    />

                    <button
                      type="button"
                      disabled={actionLoading}
                      onClick={() => handleJoinClub(null)}
                      style={secondaryButton}
                    >
                      {translate('club.find.joinByCode')}
                    </button>
                  </div>
                </div>
              </div>

              {!isPartner && (
                <div style={{ color: '#64748b', fontSize: 12 }}>
                  {translate('club.create.partnersOnly')}
                </div>
              )}
            </>
          )}

          {!loading && clubInfo.inClub && club && (
            <>
              <div className="profile-club-card" style={card}>
                <div
                  className="club-header-row"
                  style={{
                    display: 'flex',
                    gap: 20,
                    alignItems: 'center',
                    flexWrap: 'wrap',
                  }}
                >
                  <ClubImage image={clubImageUrl} name={club.name} size={110} />

                  <div style={{ flex: 1, minWidth: 240 }}>
                    <h2 style={{ margin: '0 0 6px', color: '#f8fafc' }}>
                      {club.name}
                    </h2>

                    <div style={clubMetaStyle}>
                      {translate('club.common.captain')}:{' '}
                      <strong style={{ color: '#e2e8f0' }}>
                        {club.owner?.username || '—'}
                      </strong>
                    </div>

                    <div style={clubMetaStyle}>
                      {translate('club.common.code')}:{' '}
                      <code
                        style={{
                          padding: '2px 7px',
                          borderRadius: 5,
                          border: '1px solid #334155',
                          background: '#0f172a',
                          color: '#93c5fd',
                        }}
                      >
                        {club.code}
                      </code>
                    </div>

                    <div style={{ color: '#94a3b8', fontSize: 13 }}>
                      {translate('club.common.members')}: {club.members?.length || 0}
                    </div>
                  </div>
                </div>

                {club.description && (
                  <div
                    style={{
                      marginTop: 18,
                      paddingTop: 16,
                      borderTop: '1px solid #1e293b',
                      color: '#cbd5e1',
                      lineHeight: 1.5,
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {club.description}
                  </div>
                )}
              </div>

              <div
                className="profile-club-card club-chat-card"
                style={card}
              >
                <div className="club-chat-heading">

                  <div>
                    <h3
                      style={{
                        margin: 0,
                        color: '#e5e7eb',
                      }}
                    >
                      {translate('club.chat.title')}
                    </h3>

                    <div className="club-chat-subtitle">
                      {translate('club.chat.subtitle')}
                    </div>
                  </div>


                  <div className="club-chat-tabs">

                    <button
                      type="button"
                      className={
                        'club-chat-tab' +
                        (
                          chatMode === 'club'
                            ? ' is-active'
                            : ''
                        )
                      }
                      onClick={() => {
                        setChatMode('club');
                        setChatText('');
                        fetchClubChat();
                      }}
                    >
                      {translate('club.chat.general')}
                    </button>


                    <button
                      type="button"
                      className={
                        'club-chat-tab' +
                        (
                          chatMode === 'private'
                            ? ' is-active'
                            : ''
                        )
                      }
                      onClick={() => {
                        setChatMode(
                          'private'
                        );

                        setChatText('');

                        if (
                          selectedPrivateUserId
                        ) {
                          fetchPrivateChat(
                            selectedPrivateUserId
                          );
                        }
                      }}
                    >
                      {translate('club.chat.private')}
                    </button>

                  </div>

                </div>


                <div
                  className={
                    'club-chat-layout' +
                    (
                      chatMode === 'private' &&
                      isCaptain
                        ? ' has-people'
                        : ''
                    )
                  }
                >

                  {chatMode === 'private' &&
                    isCaptain && (
                    <div className="club-private-people">

                      <div className="club-private-people__title">
                        {translate('club.members.title')}
                      </div>


                      {(club.members || [])
                        .filter(
                          (member) =>
                            Number(
                              member
                                ?.user?.id
                            ) !==
                            ownerId
                        )
                        .map(
                          (member) => {
                            const user =
                              member.user ||
                              {};

                            const active =
                              Number(
                                user.id
                              ) ===
                              Number(
                                selectedPrivateUserId
                              );

                            return (
                              <button
                                key={
                                  member.id
                                }
                                type="button"
                                className={
                                  'club-private-person' +
                                  (
                                    active
                                      ? ' is-active'
                                      : ''
                                  )
                                }
                                onClick={() => {
                                  setSelectedPrivateUserId(
                                    Number(
                                      user.id
                                    )
                                  );

                                  setChatText('');

                                  fetchPrivateChat(
                                    user.id
                                  );
                                }}
                              >
                                <ClubAvatar
                                  image={
                                    resolveImage(
                                      user.avatar
                                    )
                                  }
                                  username={
                                    user.username
                                  }
                                />

                                <span>
                                  {user.username ||
                                    translate('club.roles.player')}

                                  {member
                                    ?.permissions
                                    ?.chatBanned && (
                                    <small>
                                      {translate('club.chat.blocked')}
                                    </small>
                                  )}
                                </span>
                              </button>
                            );
                          }
                        )}

                    </div>
                  )}


                  <div className="club-chat-panel">

                    <div className="club-chat-panel__top">

                      <div>
                        <strong>
                          {chatMode === 'club'
                            ? translate('club.chat.generalTitle', { name: club.name })
                            : translate('club.chat.privateTitle', { name: privateChatTitle })}
                        </strong>

                        <span>
                          {chatMode === 'club'
                            ? translate('club.chat.generalVisibility')
                            : isCaptain
                              ? translate('club.chat.captainPrivate')
                              : translate('club.chat.memberPrivate')}
                        </span>
                      </div>


                      {chatLoading && (
                        <small>
                          {translate('club.chat.updating')}
                        </small>
                      )}

                    </div>


                    <div className="club-chat-messages">

                      {!chatLoading &&
                        currentChatMessages
                          .length === 0 && (
                        <div className="club-chat-empty">
                          {translate('club.chat.empty')}
                        </div>
                      )}


                      {currentChatMessages
                        .map(
                          (message) => {
                            const mine =
                              Number(
                                message
                                  ?.sender
                                  ?.id
                              ) ===
                              myUserId;

                            const avatar =
                              resolveImage(
                                message
                                  ?.sender
                                  ?.avatar
                              );

                            return (
                              <div
                                key={
                                  message.id
                                }
                                className={
                                  'club-chat-message' +
                                  (
                                    mine
                                      ? ' is-mine'
                                      : ''
                                  )
                                }
                              >

                                {!mine && (
                                  <ClubAvatar
                                    image={
                                      avatar
                                    }
                                    username={
                                      message
                                        ?.sender
                                        ?.username
                                    }
                                  />
                                )}


                                <div className="club-chat-bubble">

                                  <div className="club-chat-bubble__meta">
                                    <strong>
                                      {mine
                                        ? translate('club.chat.you')
                                        : (
                                            message
                                              ?.sender
                                              ?.username ||
                                            translate('club.roles.player')
                                          )}
                                    </strong>

                                    <span>
                                      {formatChatTime(
                                        message
                                          .createdAt,
                                        locale
                                      )}
                                    </span>
                                  </div>


                                  <div className="club-chat-bubble__text">
                                    {message.text}
                                  </div>

                                </div>

                              </div>
                            );
                          }
                        )}

                    </div>


                    {!canWriteCurrentChat && (
                      <div className="club-chat-restricted">
                        {myPermissions
                          .chatBanned
                          ? translate('club.chat.restrictedAll')
                          : chatMode === 'club'
                            ? translate('club.chat.restrictedGeneral')
                            : translate('club.chat.restrictedPrivate')}
                      </div>
                    )}


                    {chatMode === 'private' &&
                      isCaptain &&
                      !selectedPrivateUserId && (
                      <div className="club-chat-restricted club-chat-restricted--neutral">
                        {translate('club.chat.selectMember')}
                      </div>
                    )}


                    <div className="club-chat-compose">

                      <textarea
                        value={chatText}
                        maxLength={1000}
                        rows={2}
                        disabled={
                          !canWriteCurrentChat ||
                          chatSending ||
                          (
                            chatMode === 'private' &&
                            isCaptain &&
                            !selectedPrivateUserId
                          )
                        }
                        onChange={(event) =>
                          setChatText(
                            event.target.value
                          )
                        }
                        onKeyDown={(event) => {
                          if (
                            event.key ===
                              'Enter' &&
                            !event.shiftKey
                          ) {
                            event
                              .preventDefault();

                            if (
                              canWriteCurrentChat &&
                              chatText.trim()
                            ) {
                              sendChatMessage();
                            }
                          }
                        }}
                        placeholder={
                          canWriteCurrentChat
                            ? translate('club.chat.placeholder')
                            : translate('club.chat.unavailable')
                        }
                      />


                      <button
                        type="button"
                        disabled={
                          !canWriteCurrentChat ||
                          chatSending ||
                          !chatText.trim() ||
                          (
                            chatMode === 'private' &&
                            isCaptain &&
                            !selectedPrivateUserId
                          )
                        }
                        onClick={
                          sendChatMessage
                        }
                        style={blueButton}
                      >
                        {chatSending
                          ? translate('club.chat.sending')
                          : translate('club.chat.send')}
                      </button>

                    </div>

                  </div>

                </div>
              </div>


              {isCaptain && (
                <div className="profile-club-card" style={card}>
                  <h3 style={{ marginTop: 0, color: '#e5e7eb' }}>
                    {translate('club.management.title')}
                  </h3>

                  <div style={{ display: 'grid', gap: 14, maxWidth: 700 }}>
                    <label>
                      <div style={labelStyle}>{translate('club.common.name')}</div>
                      <input
                        value={editName}
                        maxLength={40}
                        onChange={(event) => setEditName(event.target.value)}
                        style={input}
                      />
                    </label>

                    <label>
                      <div style={labelStyle}>{translate('club.common.description')}</div>
                      <textarea
                        value={editDescription}
                        maxLength={500}
                        rows={4}
                        onChange={(event) => setEditDescription(event.target.value)}
                        style={{ ...input, resize: 'vertical' }}
                      />
                    </label>

                    <label>
                      <div style={labelStyle}>{translate('club.management.newEmblem')}</div>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={(event) =>
                          setEditImage(event.target.files?.[0] || null)
                        }
                        style={{ color: '#cbd5e1' }}
                      />
                    </label>

                    <div>
                      <button
                        type="button"
                        disabled={actionLoading}
                        onClick={handleSaveSettings}
                        style={blueButton}
                      >
                        {translate('club.management.save')}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="profile-club-card" style={card}>
                <div
                  className="club-members-heading"
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 10,
                    marginBottom: 14,
                  }}
                >
                  <h3 style={{ margin: 0, color: '#e5e7eb' }}>
                    {translate('club.members.title')}
                  </h3>

                  {isCaptain && (
                    <div style={{ color: '#94a3b8', fontSize: 13 }}>
                      {translate('club.members.balance')}:{' '}
                      <strong style={{ color: '#e5e7eb' }}>
                        {Number(clubBalance || 0).toFixed(2)}
                      </strong>
                    </div>
                  )}
                </div>

                <div style={{ display: 'grid', gap: 10 }}>
                  {(club.members || []).map((member) => {
                    const user = member.user || {};
                    const avatar = resolveImage(user.avatar);
                    const isOwner =
                      Number(user.id) === ownerId || member.role === 'captain';

                    return (
                      <div
                        key={member.id}
                        className="club-member-row"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                          padding: 12,
                          borderRadius: 10,
                          border: '1px solid #1e293b',
                          background: '#0b1220',
                          flexWrap: 'wrap',
                        }}
                      >
                        <ClubAvatar image={avatar} username={user.username} />

                        <div style={{ flex: 1, minWidth: 190 }}>
                          <div style={{ color: '#f1f5f9', fontWeight: 700 }}>
                            {user.username || translate('club.roles.player')}

                            {isOwner && (
                              <span
                                style={{
                                  marginLeft: 8,
                                  color: '#fbbf24',
                                  fontSize: 11,
                                }}
                              >
                                {translate('club.roles.captainUpper')}
                              </span>
                            )}
                          </div>

                          <div style={{ marginTop: 4, color: '#94a3b8', fontSize: 12 }}>
                            {translate('club.members.short')}: {user.short_rating ?? '—'} · {translate('club.members.long')}:{' '}
                            {user.long_rating ?? '—'}
                          </div>
                        </div>

                        {isCaptain && !isOwner && (
                          <div className="club-member-captain-controls">

                            <div className="club-member-permissions">


                              <button
                                type="button"
                                className={
                                  'club-permission-button' +
                                  (
                                    member
                                      ?.permissions
                                      ?.canCreateClubTournaments
                                      ? ' is-on'
                                      : ''
                                  )
                                }
                                disabled={
                                  permissionSavingId ===
                                  Number(
                                    member.id
                                  )
                                }
                                onClick={() =>
                                  handleClubTournamentPermissionClick(
                                    member
                                  )
                                }
                                title={translate('club.members.tournamentPermissionTitle')}
                              >
                                {translate('club.members.clubTournaments')}:{' '}
                                {member
                                  ?.permissions
                                  ?.canCreateClubTournaments
                                  ? translate('club.common.on')
                                  : translate('club.common.off')}
                              </button>


                              <button
                                type="button"
                                className={
                                  'club-permission-button' +
                                  (
                                    member
                                      ?.permissions
                                      ?.canWriteClubChat
                                      ? ' is-on'
                                      : ''
                                  )
                                }
                                disabled={
                                  permissionSavingId ===
                                  Number(
                                    member.id
                                  )
                                }
                                onClick={() =>
                                  handleMemberPermissions(
                                    member,
                                    {
                                      canWriteClubChat:
                                        !member
                                          ?.permissions
                                          ?.canWriteClubChat,
                                    }
                                  )
                                }
                              >
                                {translate('club.chat.general')}:{' '}
                                {member
                                  ?.permissions
                                  ?.canWriteClubChat
                                  ? translate('club.common.on')
                                  : translate('club.common.off')}
                              </button>


                              <button
                                type="button"
                                className={
                                  'club-permission-button' +
                                  (
                                    member
                                      ?.permissions
                                      ?.canWritePrivateChat
                                      ? ' is-on'
                                      : ''
                                  )
                                }
                                disabled={
                                  permissionSavingId ===
                                  Number(
                                    member.id
                                  )
                                }
                                onClick={() =>
                                  handleMemberPermissions(
                                    member,
                                    {
                                      canWritePrivateChat:
                                        !member
                                          ?.permissions
                                          ?.canWritePrivateChat,
                                    }
                                  )
                                }
                              >
                                {translate('club.members.private')}:{' '}
                                {member
                                  ?.permissions
                                  ?.canWritePrivateChat
                                  ? translate('club.common.on')
                                  : translate('club.common.off')}
                              </button>


                              <button
                                type="button"
                                className={
                                  'club-permission-button club-permission-button--ban' +
                                  (
                                    member
                                      ?.permissions
                                      ?.chatBanned
                                      ? ' is-banned'
                                      : ''
                                  )
                                }
                                disabled={
                                  permissionSavingId ===
                                  Number(
                                    member.id
                                  )
                                }
                                onClick={() =>
                                  handleMemberPermissions(
                                    member,
                                    {
                                      chatBanned:
                                        !member
                                          ?.permissions
                                          ?.chatBanned,
                                    }
                                  )
                                }
                              >
                                {member
                                  ?.permissions
                                  ?.chatBanned
                                  ? translate('club.members.unblockChat')
                                  : translate('club.members.blockChat')}
                              </button>


                              <button
                                type="button"
                                className="club-permission-button club-permission-button--private"
                                onClick={() =>
                                  openPrivateChat(
                                    member
                                  )
                                }
                              >
                                {translate('club.members.writePrivate')}
                              </button>

                            </div>


                            <div className="club-transfer-row">
                              <input
                                type="number"
                                min="0.01"
                                step="0.01"
                                value={transferAmounts[user.id] || ''}
                                onChange={(event) =>
                                  setTransferAmounts((previous) => ({
                                    ...previous,
                                    [user.id]: event.target.value,
                                  }))
                                }
                                placeholder={translate('club.members.amountPlaceholder')}
                                style={{ ...input, width: 120 }}
                              />

                              <button
                                type="button"
                                disabled={actionLoading}
                                onClick={() => handleTransfer(member)}
                                style={blueButton}
                              >
                                {translate('club.members.transfer')}
                              </button>
                            </div>

                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {!isCaptain && (
                  <div className="club-my-rights">

                    <strong>
                      {translate('club.rights.title')}
                    </strong>

                    <span>
                      {translate('club.rights.tournaments')}:{' '}
                      <b>
                        {myPermissions
                          .canCreateClubTournaments
                          ? translate('club.rights.allowed')
                          : translate('club.rights.notAllowed')}
                      </b>
                    </span>

                    <span>
                      {translate('club.chat.general')}:{' '}
                      <b>
                        {myPermissions
                          .canWriteClubChat &&
                        !myPermissions
                          .chatBanned
                          ? translate('club.rights.canWrite')
                          : translate('club.rights.readOnly')}
                      </b>
                    </span>

                    <span>
                      {translate('club.rights.privateWithCaptain')}:{' '}
                      <b>
                        {myPermissions
                          .canWritePrivateChat &&
                        !myPermissions
                          .chatBanned
                          ? translate('club.rights.canWrite')
                          : translate('club.rights.readOnly')}
                      </b>
                    </span>

                  </div>
                )}


                {!isCaptain && (
                  <div style={{ marginTop: 18 }}>
                    <button
                      type="button"
                      disabled={actionLoading}
                      onClick={handleLeaveClub}
                      style={redButton}
                    >
                      {translate('club.leave')}
                    </button>
                  </div>
                )}
              </div>

              {isCaptain && (
                <div className="profile-club-card" style={card}>
                  <h3 style={{ marginTop: 0, color: '#e5e7eb' }}>
                    {translate('club.transfers.title')}
                  </h3>

                  {transfers.length === 0 ? (
                    <div style={{ color: '#64748b', fontSize: 13 }}>
                      {translate('club.transfers.empty')}
                    </div>
                  ) : (
                    <div
                      className="club-transfers-table"
                      style={{
                        overflowX: 'auto',
                        borderRadius: 10,
                        border: '1px solid #1e293b',
                      }}
                    >
                      <table
                        style={{
                          width: '100%',
                          borderCollapse: 'collapse',
                          fontSize: 13,
                        }}
                      >
                        <thead>
                          <tr>
                            <th style={thStyle}>{translate('club.transfers.date')}</th>
                            <th style={thStyle}>{translate('club.transfers.recipient')}</th>
                            <th style={{ ...thStyle, textAlign: 'right' }}>{translate('club.transfers.amount')}</th>
                          </tr>
                        </thead>

                        <tbody>
                          {transfers.map((row) => (
                            <tr key={row.id}>
                              <td style={tdStyle}>
                                {row.createdAt
                                  ? new Date(row.createdAt).toLocaleString(locale)
                                  : '—'}
                              </td>

                              <td style={tdStyle}>{row.receiver?.username || '—'}</td>

                              <td
                                style={{
                                  ...tdStyle,
                                  textAlign: 'right',
                                  color: '#22c55e',
                                  fontWeight: 700,
                                }}
                              >
                                {Number(row.amount || 0).toFixed(2)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {tournamentPermissionConfirm && (
        <div
          role="presentation"
          onMouseDown={(event) => {
            if (
              event.target ===
              event.currentTarget &&
              permissionSavingId === null
            ) {
              setTournamentPermissionConfirm(
                null
              );
            }
          }}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 14,
            background: 'rgba(2, 6, 23, 0.78)',
            backdropFilter: 'blur(4px)',
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="club-tournament-permission-title"
            style={{
              width: 'min(560px, calc(100vw - 28px))',
              maxHeight: 'calc(100vh - 28px)',
              overflowY: 'auto',
              borderRadius: 16,
              border: '1px solid rgba(245, 158, 11, 0.38)',
              background: '#111827',
              boxShadow: '0 24px 80px rgba(0,0,0,.55)',
              color: '#e5e7eb',
            }}
          >
            <div
              style={{
                padding: '20px 20px 14px',
                borderBottom: '1px solid #273244',
              }}
            >
              <div
                style={{
                  marginBottom: 8,
                  color: '#fbbf24',
                  fontSize: 12,
                  fontWeight: 900,
                  letterSpacing: 1.2,
                }}
              >
                {translate('club.confirm.tournamentImportant')}
              </div>

              <h3
                id="club-tournament-permission-title"
                style={{
                  margin: 0,
                  fontSize: 22,
                  lineHeight: 1.25,
                }}
              >
                {translate('club.confirm.tournamentQuestion')}
              </h3>
            </div>

            <div
              style={{
                padding: 20,
              }}
            >
              <div
                style={{
                  marginBottom: 16,
                  padding: 14,
                  borderRadius: 12,
                  background: 'rgba(245, 158, 11, 0.08)',
                  border: '1px solid rgba(245, 158, 11, 0.28)',
                  color: '#fde68a',
                  lineHeight: 1.55,
                }}
              >
                {translate('club.confirm.tournamentGrantBefore')}{' '}
                <b>
                  {tournamentPermissionConfirm
                    ?.user
                    ?.username || translate('club.roles.member')}
                </b>{' '}
                {translate('club.confirm.tournamentGrantAfter')}
              </div>

              <p
                style={{
                  margin: '0 0 12px',
                  color: '#d1d5db',
                  lineHeight: 1.6,
                }}
              >
                {translate('club.confirm.tournamentReserveBefore')}{' '}
                <b>{translate('club.confirm.tournamentReserveStrong')}</b>.
              </p>

              <p
                style={{
                  margin: '0 0 12px',
                  color: '#9ca3af',
                  lineHeight: 1.6,
                }}
              >
                {translate('club.confirm.tournamentDisable')}
              </p>

              <p
                style={{
                  margin: '0 0 20px',
                  color: '#9ca3af',
                  lineHeight: 1.6,
                }}
              >
                {translate('club.confirm.tournamentPersonal')}
              </p>

              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 10,
                  justifyContent: 'flex-end',
                }}
              >
                <button
                  type="button"
                  disabled={
                    permissionSavingId !==
                    null
                  }
                  onClick={() =>
                    setTournamentPermissionConfirm(
                      null
                    )
                  }
                  style={{
                    ...secondaryButton,
                    minWidth: 140,
                  }}
                >
                  {translate('club.common.cancel')}
                </button>

                <button
                  type="button"
                  disabled={
                    permissionSavingId !==
                    null
                  }
                  onClick={
                    confirmClubTournamentPermission
                  }
                  style={{
                    ...blueButton,
                    minWidth: 210,
                    background: '#d99a24',
                    color: '#111827',
                    fontWeight: 900,
                  }}
                >
                  {permissionSavingId !== null
                    ? translate('club.common.saving')
                    : translate('club.confirm.allow')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function formatChatTime(value, locale) {
  if (!value) {
    return '';
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return '';
  }

  return date.toLocaleString(
    locale,
    {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }
  );
}


function ClubImage({ image, name, size }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        flex: '0 0 auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        borderRadius: size >= 100 ? 18 : 12,
        border: '1px solid #334155',
        background: '#0f172a',
      }}
    >
      {image ? (
        <img
          src={image}
          alt={name || ''}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        <span
          style={{
            color: '#64748b',
            fontSize: size >= 100 ? 42 : 28,
            fontWeight: 800,
          }}
        >
          {String(name || 'C').slice(0, 1).toUpperCase()}
        </span>
      )}
    </div>
  );
}

function ClubAvatar({ image, username }) {
  return (
    <div
      style={{
        width: 46,
        height: 46,
        flex: '0 0 auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        borderRadius: '50%',
        background: '#1e293b',
      }}
    >
      {image ? (
        <img
          src={image}
          alt=""
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        <span style={{ color: '#64748b', fontWeight: 700 }}>
          {String(username || '?').slice(0, 1).toUpperCase()}
        </span>
      )}
    </div>
  );
}

const labelStyle = {
  marginBottom: 5,
  color: '#9ca3af',
  fontSize: 12,
};

const counterStyle = {
  marginTop: 4,
  color: '#64748b',
  fontSize: 11,
  textAlign: 'right',
};

const smallMutedStyle = {
  marginBottom: 3,
  color: '#94a3b8',
  fontSize: 12,
};

const clubMetaStyle = {
  marginBottom: 6,
  color: '#94a3b8',
  fontSize: 13,
};

const thStyle = {
  padding: '10px 12px',
  borderBottom: '1px solid #1e293b',
  background: '#0f172a',
  color: '#94a3b8',
  fontWeight: 500,
  textAlign: 'left',
};

const tdStyle = {
  padding: '10px 12px',
  borderBottom: '1px solid #0f172a',
  color: '#e2e8f0',
};
